import '../styles/SpendingTimeline.css';

import React, {useEffect, useState} from 'react';
import { BarStack } from '@visx/shape';
import { Group } from '@visx/group';
import { Grid } from '@visx/grid';
import {AxisBottom, AxisLeft} from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { LegendOrdinal } from '@visx/legend';
import {useSearchParams} from "react-router-dom";
import useAxiosRequest from "../hooks/useAxiosRequest";
import config from "../config.json";

const formatLocation = (location) => location.split('-')[1];

const SpendingBarChart = () => {
    const [ searchParams ] = useSearchParams();

    const { data, isLoading } = useAxiosRequest({
        url: `${config.backendUrl}/charts/data?id=${searchParams.get("data")}`,
        withCredentials: true,
        method: "get"
    });

    const [ barChartData, setBarChartData ] = useState(null);

    const [ colorScale, setColorScale ] = useState(null);

    const [ spentScale, setSpentScale ] = useState(null);

    const [ locationScale, setLocationScale ] = useState(null);

    const [ isProcessing, setProcessing ] = useState(true);

    useEffect(() => {
        if (!data) return;

        const barChartData = data.locations.map(location => Object.fromEntries([['location', location.path], ...data.parties.map(party => [party.code, data.events.filter(event => event.party === party.code && event.location.startsWith(location.path)).reduce((sum, event) => sum + event.spent, 0)])]));

        setBarChartData(barChartData);

        const spentScale = scaleLinear({
            domain: [0, Math.max(...barChartData.map(locationData => data.parties.reduce((sum, party) => sum + locationData[party.code], 0)))],
            nice: true,
        });

        setSpentScale([spentScale]);

        const colorScale = scaleOrdinal({
            domain: data.parties.filter(party => data.events.find(event => event.party === party.code)).map(party => party.code),
            range: data.parties.filter(party => data.events.find(event => event.party === party.code)).map(party => party.color)
        });

        setColorScale([colorScale]);

        const locationScale = scaleBand({
            domain: data.locations.map(location => location.path),
            padding: 0.2,
        });

        setLocationScale([locationScale]);

        setProcessing(false);
    }, [data]);

    if (isProcessing) return;

    locationScale[0].rangeRound([0, 650]);
    spentScale[0].range([300, 0]);

    return (
        <div id={"chart"} style={{ width: "800px", height: "400px", position: "absolute" }}>
            <div style={{ position: "relative", top: "15px", width: "800px", display: "flex", justifyContent: "center", fontWeight: "500", fontSize: "16px" }}>
                <LegendOrdinal
                    scale={colorScale[0]}
                    labelFormat={(code) => {
                        const party = data.parties.find(party => party.code === code);
                        return party?.shortName || party?.name || code;
                    }}
                    direction="row"
                    labelMargin="0 15px 0 0"
                />
            </div>
            <svg width={800} height={400} style={{ position: "relative", top: "30px" }}>
                <rect
                    x={0}
                    y={0}
                    width={800}
                    height={400}
                    fill="white"
                />
                <Grid
                    top={10}
                    left={100}
                    xScale={locationScale[0]}
                    yScale={spentScale[0]}
                    width={650}
                    height={400}
                    stroke="black"
                    strokeOpacity={0.1}
                />
                <Group left={100} top={10}>
                    <BarStack
                        data={barChartData}
                        keys={data.parties.map(party => party.code)}
                        x={(d) => d.location}
                        xScale={locationScale[0]}
                        yScale={spentScale[0]}
                        color={colorScale[0]}
                    >
                        {(barStacks) =>
                            barStacks.map((barStack) =>
                                barStack.bars.map((bar) => (
                                    <rect
                                        key={`bar-stack-${barStack.index}-${bar.index}`}
                                        x={bar.x}
                                        y={bar.y}
                                        height={bar.height}
                                        width={bar.width}
                                        fill={bar.color}
                                    />
                                )),
                            )
                        }
                    </BarStack>
                </Group>
                <AxisBottom
                    top={310}
                    left={100}
                    scale={locationScale[0]}
                    tickFormat={formatLocation}
                    stroke={'black'}
                    tickStroke={'black'}
                    tickLabelProps={{
                        fill: 'black',
                        fontSize: 11,
                        textAnchor: 'middle',
                    }}
                />
                <AxisLeft
                    top={10}
                    left={100}
                    scale={spentScale[0]}
                    tickFormat={(v) => "$" + v.toLocaleString()}
                    stroke={'black'}
                    tickStroke={'black'}
                    tickLabelProps={{
                        fill: 'black',
                        fontSize: 11,
                    }}
                />
            </svg>
        </div>
    );
}

export default SpendingBarChart;
